import { z } from 'zod';
import { agentChannelSchema } from '../agent/types';

// =============================================================================
// Assignment Types
// =============================================================================

export const assignToTypeSchema = z.enum(['agent', 'team', 'user', 'unassigned']);
export type AssignToType = z.infer<typeof assignToTypeSchema>;

/** A task type is created/updated with a real assignee; 'unassigned' is read-only. */
export const initialAssignToTypeSchema = assignToTypeSchema.exclude(['unassigned']);

// =============================================================================
// Rule Conditions
// =============================================================================

export const comparisonOpSchema = z.enum(['lt', 'lte', 'eq', 'gte', 'gt', 'neq']);
export type ComparisonOp = z.infer<typeof comparisonOpSchema>;

export const ruleConditionsSchema = z.object({
  outcome: z.union([z.string(), z.array(z.string())]).optional(),
  channel: agentChannelSchema.optional(),
  channelAttempts: z
    .object({
      op: comparisonOpSchema,
      value: z.number().int().min(0),
    })
    .optional(),
  totalAttempts: z
    .object({
      op: comparisonOpSchema,
      value: z.number().int().min(0),
    })
    .optional(),
  collectedData: z
    .object({
      field: z.string(),
      op: z.enum(['exists', 'not_exists', 'equals', 'contains']),
      value: z.unknown().optional(),
    })
    .optional(),
  timeSinceLastAttempt: z
    .object({
      op: z.enum(['lt', 'gte']),
      minutes: z.number().int().positive(),
    })
    .optional(),
});

export type RuleConditions = z.infer<typeof ruleConditionsSchema>;

// =============================================================================
// Rule Actions
// =============================================================================

export const ruleActionSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('retry'),
    delayMinutes: z.number().int().positive(),
  }),
  z.object({
    type: z.literal('switch_channel'),
    channel: agentChannelSchema,
    assignToType: z.enum(['agent', 'team', 'user']),
    assignToId: z.string().uuid(),
  }),
  z.object({
    type: z.literal('leave_voicemail'),
    template: z.string().optional(),
  }),
  z.object({
    type: z.literal('escalate'),
    assignToType: z.enum(['team', 'user']),
    assignToId: z.string().uuid(),
    priority: z.enum(['low', 'normal', 'high', 'urgent']).optional(),
  }),
  z.object({
    type: z.literal('complete'),
    status: z.enum(['success', 'failed']),
    reason: z.string().optional(),
  }),
  z.object({
    type: z.literal('reschedule'),
    delayMinutes: z.number().int().positive().optional(),
    useCollectedField: z.string().optional(),
  }),
  z.object({
    type: z.literal('assign'),
    assignToType: z.enum(['agent', 'team', 'user']),
    assignToId: z.string().uuid(),
  }),
  z.object({
    type: z.literal('notify'),
    targetType: z.enum(['user', 'team']),
    targetId: z.string().uuid(),
    messageTemplate: z.string().min(1),
  }),
]);

export type RuleAction = z.infer<typeof ruleActionSchema>;

// =============================================================================
// Workflow Rule
// =============================================================================

export const workflowRuleSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  description: z.string().optional(),
  order: z.number().int().min(0),
  conditions: ruleConditionsSchema,
  actions: z.array(ruleActionSchema).min(1),
  isEnabled: z.boolean().optional(),
});

export type WorkflowRule = z.infer<typeof workflowRuleSchema>;

// =============================================================================
// Task Type Summary Response
// =============================================================================

export const taskTypeSummarySchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  description: z.string().optional(),
  isSystem: z.boolean(),
  initialChannel: agentChannelSchema,
  ruleCount: z.number(),
});

export type TaskTypeSummaryResponse = z.infer<typeof taskTypeSummarySchema>;

// =============================================================================
// Task Type Full Response
// =============================================================================

export const taskTypeResponseSchema = z.object({
  id: z.string(),
  organizationId: z.string().nullable(),
  code: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  isSystem: z.boolean(),
  initialChannel: agentChannelSchema,
  initialAssignToType: assignToTypeSchema,
  initialAssignToId: z.string(),
  workflowRules: z.array(workflowRuleSchema),
  defaultRule: workflowRuleSchema,
  workflowPrompt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type TaskTypeResponse = z.infer<typeof taskTypeResponseSchema>;

// =============================================================================
// Create/Update Requests
// =============================================================================

export const createTaskTypeRequestSchema = z.object({
  code: z
    .string()
    .min(1, 'Code is required')
    .regex(/^[a-z][a-z0-9_]*$/, 'Code must be lowercase with underscores'),
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
  initialChannel: agentChannelSchema,
  initialAssignToType: initialAssignToTypeSchema,
  initialAssignToId: z.string().uuid(),
  workflowRules: z.array(workflowRuleSchema),
  defaultRule: workflowRuleSchema,
  workflowPrompt: z.string().optional(),
});

export type CreateTaskTypeRequest = z.infer<typeof createTaskTypeRequestSchema>;

export const updateTaskTypeRequestSchema = z.object({
  name: z.string().min(1).optional(),
  description: z.string().optional(),
  initialChannel: agentChannelSchema.optional(),
  initialAssignToType: initialAssignToTypeSchema.optional(),
  initialAssignToId: z.string().uuid().optional(),
  workflowRules: z.array(workflowRuleSchema).optional(),
  defaultRule: workflowRuleSchema.optional(),
  workflowPrompt: z.string().optional(),
});

export type UpdateTaskTypeRequest = z.infer<typeof updateTaskTypeRequestSchema>;

// =============================================================================
// Validation
// =============================================================================

export const validateRulesRequestSchema = z.object({
  workflowRules: z.array(workflowRuleSchema),
  defaultRule: workflowRuleSchema,
});

export type ValidateRulesRequest = z.infer<typeof validateRulesRequestSchema>;

export const validateRulesResponseSchema = z.object({
  valid: z.boolean(),
  errors: z.array(z.string()),
});

export type ValidateRulesResponse = z.infer<typeof validateRulesResponseSchema>;

// =============================================================================
// Search
// =============================================================================

export const taskTypeSearchRequestSchema = z.object({
  limit: z.number().int().min(1).max(100).optional(),
  offset: z.number().int().min(0).optional(),
  searchTerm: z.string().optional(),
  includeSystem: z.boolean().optional(),
});

export type TaskTypeSearchRequest = z.infer<typeof taskTypeSearchRequestSchema>;
